import { Directive, ElementRef, forwardRef, HostListener, Input, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import flatpickr from 'flatpickr';
import moment from 'moment';
import { StudioConfigService } from '../services/studio-config.service';
import { LocalizationService, CountryDateFormats } from '../services/localization.service';
import { take } from 'rxjs/operators';
import { LoggerService } from '../services/logger.service';

@Directive({
  selector: '[appFlatpickrDatetime]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FlatpickrDatetimeDirective),
      multi: true
    }
  ]
})
export class FlatpickrDatetimeDirective implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit {
  @Input() enableTime: boolean = true;
  @Input() minuteIncrement: number = 15;
  @Input() setCurrentTimeOnOpen: boolean = false; // Auto-set current time if empty
  @Input() closeOnSelect: boolean = true; // Auto-close picker after selection
  @Input() appendToSelector?: string;

  private flatpickrInstance?: flatpickr.Instance;
  private studioCountry: string = 'US';
  private formats?: CountryDateFormats;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private isDisabled: boolean = false;
  private initialValue: any = null; // Store value set before initialization
  private currentValue: any = null; // Track current value
  // True only for the brief synchronous window of a day-grid selection. A
  // capture-phase listener on flatpickr's daysContainer sets it the instant
  // the user picks a day (click or Enter), and onChange consumes+resets it.
  // selectDate() fires onChange synchronously (index.js:1602) so the flag is
  // still set when read; every time-spinner edit goes through a 300ms debounce
  // (index.js:142,270) so the flag is already false by the time its onChange
  // runs. This is why we can't use document.activeElement: selectDate()
  // force-focuses the hour input (index.js:1591) before firing onChange, so
  // focus is inside the time container on a *day* click too.
  private dayWasPicked = false;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private studioConfigService: StudioConfigService,
    private localizationService: LocalizationService
  ) {}

  ngOnInit(): void {
    // Load studio country for formatting
    this.studioConfigService.getCountryCode().pipe(take(1)).subscribe((country) => {
      if (country) {
        this.studioCountry = country;
        this.formats = this.localizationService.getDateFormatsForCountry(country);

        // Update format if instance already exists
        if (this.flatpickrInstance && this.formats) {
          const dateFormat = this.enableTime
            ? `${this.formats.flatpickrDateFormat} h:i K`
            : this.formats.flatpickrDateFormat;
          this.flatpickrInstance.set('dateFormat', dateFormat);
        }
      }
    });
  }

  private getAppendToElement(): HTMLElement | undefined {
    if (this.appendToSelector) {
      const customElement = document.querySelector(this.appendToSelector);
      if (customElement instanceof HTMLElement) {
        return customElement;
      }
    }

    const overlaySelectors = [
      '.mat-mdc-dialog-container',
      '.mat-dialog-container',
      '.cdk-overlay-pane',
      '.mat-dialog-scrollable',
      '.dialog-wrapper',
    ];

    for (const selector of overlaySelectors) {
      const closest = this.el.nativeElement.closest(selector);
      if (closest instanceof HTMLElement) {
        return closest;
      }
    }

    return undefined;
  }

  ngAfterViewInit(): void {
    // Small delay to ensure the input is fully rendered
    setTimeout(() => {
      this.initializeFlatpickr();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
  }

  // The input is readonly, so flatpickr ignores keystrokes and a date can't be
  // cleared by typing. Intercept Backspace/Delete here and clear the picker:
  // flatpickr.clear() fires onChange([]), which propagates null to the form
  // control (see the onChange else-branch) so the empty value is saved.
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.isDisabled) {
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      if (this.flatpickrInstance) {
        this.flatpickrInstance.clear();
      }
    }
  }

  private initializeFlatpickr(): void {
    if (!this.formats) {
      this.formats = this.localizationService.getDateFormatsForCountry(this.studioCountry);
    }

    const dateFormat = this.enableTime
      ? `${this.formats.flatpickrDateFormat} h:i K`
      : this.formats.flatpickrDateFormat;

    // Determine default date from initial value
    let defaultDate = undefined;
    if (this.initialValue) {
      defaultDate = moment(this.initialValue).toDate();
    }

    const appendToElement = this.getAppendToElement();

    this.flatpickrInstance = flatpickr(this.el.nativeElement, {
      dateFormat: dateFormat,
      enableTime: this.enableTime,
      disableMobile: true,
      clickOpens: true,
      allowInput: false,
      minuteIncrement: this.minuteIncrement,
      defaultDate: defaultDate,
      appendTo: appendToElement || undefined,
      formatDate: (date) => this.formatDateForDisplay(date),
      onReady: (_selectedDates, _dateStr, instance) => {
        // Mark day-grid selections so onChange can distinguish them from
        // time-spinner edits. Capture phase fires before flatpickr's own
        // bubble-phase click->selectDate (index.js:294), so the flag is set
        // by the time the synchronous selection onChange reads it. The
        // daysContainer is created once and only refilled on navigation
        // (index.js:548-560), so binding here once is enough.
        const days = instance.daysContainer;
        if (days) {
          // Only flag when the target is an actually-selectable day, mirroring
          // flatpickr's own isSelectable test (index.js:1535-1543). A click on a
          // disabled/blank cell or the gap between cells makes selectDate return
          // early WITHOUT firing onChange. Flagging those would leave a stale
          // true that the next (possibly time-edit) onChange would wrongly
          // consume and close on.
          const isSelectableDay = (target: EventTarget | null): boolean => {
            const el = target instanceof Element ? target.closest('.flatpickr-day') : null;
            return !!el
              && !el.classList.contains('flatpickr-disabled')
              && !el.classList.contains('notAllowed');
          };
          days.addEventListener('click', (e: MouseEvent) => {
            if (isSelectableDay(e.target)) {
              this.dayWasPicked = true;
            }
          }, true);
          days.addEventListener('keydown', (e: KeyboardEvent) => {
            // Enter/Space on a focused day cell routes through selectDate
            // (index.js:1060).
            if ((e.key === 'Enter' || e.key === ' ') && isSelectableDay(e.target)) {
              this.dayWasPicked = true;
            }
          }, true);
        }
      },
      onOpen: (selectedDates: Date[], dateStr, instance) => {
        // If setCurrentTimeOnOpen is enabled and no value exists, set current time
        if (this.setCurrentTimeOnOpen && !this.currentValue && selectedDates.length === 0) {
          const currentDate = moment();
          const minutes = Math.floor(currentDate.minutes() / this.minuteIncrement) * this.minuteIncrement;
          const roundedDate = currentDate.clone()
            .minutes(minutes)
            .seconds(0)
            .milliseconds(0);

          const formatted = this.enableTime
            ? roundedDate.format('YYYY-MM-DDTHH:mm:ss')
            : roundedDate.format('YYYY-MM-DD');

          this.currentValue = formatted;
          instance.setDate(roundedDate.toDate(), false);
          this.onChange(formatted);
        }
      },
      onChange: (selectedDates: Date[], dateStr, instance) => {
        if (selectedDates && selectedDates.length > 0) {
          const selectedDate = selectedDates[0];
          const formatted = this.enableTime
            ? moment(selectedDate).format('YYYY-MM-DDTHH:mm:ss')
            : moment(selectedDate).format('YYYY-MM-DD');

          this.currentValue = formatted;
          this.onChange(formatted);
          this.onTouched();

          // Auto-close ONLY when this change came from picking a day in the
          // grid. flatpickr fires onChange for time-spinner ticks too (arrows,
          // AM/PM, typing, keyboard, mousewheel), and those must keep the
          // picker open so the user can keep adjusting. dayWasPicked is set by
          // the capture-phase listeners in onReady and is only true during a
          // day selection's synchronous onChange; time edits are debounced so
          // it is already false for them. Always reset it so a stale true can
          // never leak into a later time edit.
          if (this.closeOnSelect && this.dayWasPicked) {
            instance.close();
          }
          this.dayWasPicked = false;
        } else {
          // Field was cleared. Propagate null to the form control so the
          // cleared value is persisted on save (otherwise the old date sticks).
          this.currentValue = null;
          this.dayWasPicked = false;
          this.onChange(null);
          this.onTouched();
        }
      }
    });

    // Clear the initial value as it's now set
    this.initialValue = null;

    // Set disabled state
    if (this.isDisabled) {
      this.setDisabledState(true);
    }
  }

  private formatDateForDisplay(date: Date): string {
    try {
      if (!this.formats) {
        this.formats = this.localizationService.getDateFormatsForCountry(this.studioCountry);
      }

      if (this.enableTime) {
        // Use displayDateTimeFormatMoment: "dddd, M/D/YYYY, h:mm A" for US
        // or "dddd, DD/MM/YYYY, h:mm A" for Australia
        const momentFormat = this.formats.displayDateTimeFormatMoment || 'dddd, MM/DD/YYYY, h:mm A';
        return moment(date).format(momentFormat);
      } else {
        // Just date
        const momentFormat = this.formats.calendarDateFormat || 'MM/DD/YYYY';
        return moment(date).format(momentFormat);
      }
    } catch (error) {
      LoggerService.warn('Failed to format date for display', error);
      return this.enableTime
        ? moment(date).format('YYYY-MM-DD HH:mm')
        : moment(date).format('YYYY-MM-DD');
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.currentValue = value; // Track current value

    if (value) {
      const date = moment(value).toDate();
      if (this.flatpickrInstance) {
        // Instance exists, set the date
        this.flatpickrInstance.setDate(date, false);
      } else {
        // Instance doesn't exist yet, store for later
        this.initialValue = value;
      }
    } else {
      if (this.flatpickrInstance) {
        this.flatpickrInstance.clear();
      } else {
        // Clear initial value if set
        this.initialValue = null;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.flatpickrInstance) {
      if (isDisabled) {
        this.flatpickrInstance.set('clickOpens', false);
        this.el.nativeElement.disabled = true;
      } else {
        this.flatpickrInstance.set('clickOpens', true);
        this.el.nativeElement.disabled = false;
      }
    }
  }
}

